/**
 * A synthesized arpeggio — no audio asset to fetch.
 *
 * Browsers keep an AudioContext suspended until a user gesture. Inside a click
 * (the sound toggle's preview, an approve that returned fast) resume() settles
 * at once and the chime plays. Without one — the counterparty path, or an
 * approval whose tx outlived the gesture window — resume() stays pending, and
 * Chrome settles it on the user's NEXT click. Notes queued now would play
 * then, at a random and unrelated moment, so anything that doesn't resume
 * promptly is dropped: a late chime is worse than none.
 */
let ctx: AudioContext | null = null;

/** How long a resume() may take and still count as "in the moment". */
const RESUME_GRACE_MS = 250;

function schedule(audio: AudioContext, notes: number[]) {
  const start = audio.currentTime + 0.02;
  notes.forEach((freq, i) => {
    const osc = audio.createOscillator();
    const gain = audio.createGain();
    const t0 = start + i * 0.09;
    osc.type = "triangle";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(0.18, t0 + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.35);
    osc.connect(gain).connect(audio.destination);
    osc.start(t0);
    osc.stop(t0 + 0.4);
  });
}

export function playChime(notes: number[]): void {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    ctx ??= new Ctor();
    const audio = ctx;
    if (audio.state === "running") {
      schedule(audio, notes);
      return;
    }
    const requestedAt = Date.now();
    audio
      .resume()
      .then(() => {
        if (audio.state === "running" && Date.now() - requestedAt <= RESUME_GRACE_MS) {
          schedule(audio, notes);
        }
      })
      .catch(() => {});
  } catch {
    /* no audio — the celebration still shows */
  }
}
